import type { DataProvider } from "ra-core";
import Papa from "papaparse";
import type { ParsedSpreadsheet } from "./importParser";
import type { Company, Contact, Deal, DealPipeline, Tag } from "../types";

export type DuplicateStrategy = "merge" | "create_new" | "skip";

export type ImportType = "person" | "company" | "deal";

export interface ImportOptions {
  mapping: Record<string, string>; // columnHeader -> fieldId (ex: "E-mail" -> "person.email_work")
  duplicateStrategy: DuplicateStrategy;
  importType?: ImportType; // Tipo de entidade principal a importar (default: "deal")
  createDeals?: boolean; // Se deve gerar oportunidade/negócio no Kanban (default: true)
  pipelines?: DealPipeline[]; // Lista de funis para resolução de etapa e funil por nome/id
  defaultFunilId?: string;
  defaultStageId?: string;
  defaultSalesId?: string | number | null;
  defaultOrigem?: string | null;
  defaultTemperatura?: string | null;
  dealCustomFieldsDefaults?: Record<string, any>;
  dataProvider: DataProvider;
  onProgress?: (progress: ImportProgress) => void;
}

export interface ImportProgress {
  current: number;
  total: number;
  percent: number;
  companiesCreated: number;
  companiesUpdated: number;
  contactsCreated: number;
  contactsUpdated: number;
  dealsCreated: number;
  skippedCount: number;
  statusText: string;
}

export interface ImportBatchRollbackInfo {
  importId: string;
  timestamp: string;
  fileName: string;
  dealIds: (number | string)[];
  contactIds: (number | string)[];
  companyIds: (number | string)[];
  totalCreated: number;
}

export interface ImportResult {
  total: number;
  processed: number;
  companiesCreated: number;
  companiesUpdated: number;
  contactsCreated: number;
  contactsUpdated: number;
  dealsCreated: number;
  skippedCount: number;
  hasErrors: boolean;
  skippedCsvContent?: string;
  rollbackInfo?: ImportBatchRollbackInfo;
}

/**
 * Converte valores em formato numérico brasileiro ou internacional para number
 */
function parseCurrencyOrNumber(val: any): number {
  if (typeof val === "number") return val;
  if (!val) return 0;
  const str = String(val).replace(/R\$/gi, "").replace(/\s+/g, "").trim();
  // Se contiver vírgula como decimal (ex: 3.500,00 ou 3500,00)
  if (str.includes(",")) {
    const cleaned = str.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  }
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
}

/**
 * Divide nome completo em Primeiro Nome e Sobrenome
 */
function splitFullName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) {
    return { firstName: parts[0] || "", lastName: "" };
  }
  const firstName = parts[0];
  const lastName = parts.slice(1).join(" ");
  return { firstName, lastName };
}

/**
 * Extrai lista de e-mails de uma célula
 * Suporta múltiplos e-mails separados por vírgula (,), ponto-e-vírgula (;),
 * barra (/), pipe (|), espaço ou quebra de linha.
 */
function parseEmailList(
  raw: any,
  type: string = "Work",
): Array<{ email: string; type: string }> {
  if (!raw) return [];
  const parts = String(raw)
    .split(/[\s,;|/\n\r]+/)
    .map((s) => s.replace(/^[<"']+|[>"']+$/g, "").trim())
    .filter(Boolean);

  const results: Array<{ email: string; type: string }> = [];
  for (const part of parts) {
    if (part.includes("@") && part.includes(".")) {
      const cleanEmail = part.toLowerCase();
      if (!results.some((r) => r.email === cleanEmail)) {
        results.push({ email: cleanEmail, type });
      }
    }
  }
  return results;
}

/**
 * Extrai lista de números de telefone de uma célula
 * Suporta múltiplos telefones separados por vírgula (,), ponto-e-vírgula (;),
 * barra (/ ou \), pipe (|), quebra de linha ou conectivos (" ou ", " e ").
 */
function parsePhoneList(
  raw: any,
  type: string = "Work",
): Array<{ number: string; type: string }> {
  if (!raw) return [];
  const rawStr = String(raw);

  // Divide por vírgula, ponto-e-vírgula, barras, pipes, quebras de linha ou palavras " ou " / " e "
  const parts = rawStr
    .split(/(?:[\n\r,;|/\\]+|\s+ou\s+|\s+e\s+)/i)
    .map((s) => s.trim())
    .filter(Boolean);

  const results: Array<{ number: string; type: string }> = [];
  for (const part of parts) {
    const trimmedPart = part.replace(/^[-–—:.,\s]+|[-–—:.,\s]+$/g, "").trim();
    const digitsOnly = trimmedPart.replace(/\D/g, "");
    if (digitsOnly.length >= 4) {
      if (!results.some((p) => p.number.replace(/\D/g, "") === digitsOnly)) {
        results.push({ number: trimmedPart, type });
      }
    }
  }
  return results;
}

const TAG_PALETTE = [
  "#fde2e4",
  "#fad2e1",
  "#c5dedd",
  "#dbe7e4",
  "#f0efeb",
  "#d6e2e9",
  "#bcd4e6",
  "#99c1de",
  "#bbf7d0",
  "#fef08a",
  "#fed7aa",
  "#e9d5ff",
  "#eddcd2",
  "#fff1e6",
];

/**
 * Normaliza e resolve strings de tags para IDs numéricos no CRM.
 * Suporta separadores: ponto e vírgula (;), vírgula (,), quebra de linha ou barra vertical (|).
 * Se a etiqueta não existir no CRM, ela é criada automaticamente com uma cor da paleta.
 */
async function resolveTagIds(
  rawTagInput: any,
  tagCache: Map<string, Tag>,
  dataProvider: DataProvider,
): Promise<number[]> {
  if (!rawTagInput) return [];

  let tagNames: string[] = [];
  if (Array.isArray(rawTagInput)) {
    tagNames = rawTagInput.map((t) => String(t).trim()).filter(Boolean);
  } else if (
    typeof rawTagInput === "string" ||
    typeof rawTagInput === "number"
  ) {
    tagNames = String(rawTagInput)
      .split(/[;,\n\r|]/)
      .map((t) => t.trim())
      .filter(Boolean);
  }

  if (tagNames.length === 0) return [];

  const resolvedIds: number[] = [];

  for (const name of tagNames) {
    const norm = name.toLowerCase();

    // 1. Procurar no cache em memória
    let matchedTag = tagCache.get(norm);

    // 2. Se não estiver no cache, consulta via API com filtro ilike
    if (!matchedTag) {
      try {
        const res = await dataProvider.getList("tags", {
          filter: { "name@ilike": name },
          pagination: { page: 1, perPage: 10 },
        });
        if (res.data && res.data.length > 0) {
          matchedTag =
            res.data.find((t: Tag) => t.name.trim().toLowerCase() === norm) ||
            res.data[0];
          if (matchedTag) {
            tagCache.set(norm, matchedTag);
          }
        }
      } catch (err) {
        console.warn("Aviso ao buscar tag existente:", name, err);
      }
    }

    // 3. Se ainda não existir no CRM, cria a nova etiqueta automaticamente
    if (!matchedTag) {
      try {
        const color = TAG_PALETTE[tagCache.size % TAG_PALETTE.length];
        const res = await dataProvider.create("tags", {
          data: {
            name,
            color,
          },
        });
        matchedTag = Array.isArray(res.data) ? res.data[0] : res.data;
        if (matchedTag) {
          tagCache.set(norm, matchedTag);
        }
      } catch (err) {
        console.warn("Aviso ao criar tag no CRM:", name, err);
      }
    }

    if (matchedTag && matchedTag.id !== undefined && matchedTag.id !== null) {
      const numId = Number(matchedTag.id);
      if (!resolvedIds.includes(numId)) {
        resolvedIds.push(numId);
      }
    }
  }

  return resolvedIds;
}

/**
 * Motor central de importação em lotes
 */
export async function executeImport(
  spreadsheet: ParsedSpreadsheet,
  options: ImportOptions,
): Promise<ImportResult> {
  const { mapping, duplicateStrategy, dataProvider, onProgress } = options;
  const importType: ImportType = options.importType ?? "deal";
  const total = spreadsheet.rawRows.length;
  const BATCH_SIZE = 10;

  let companiesCreated = 0;
  let companiesUpdated = 0;
  let contactsCreated = 0;
  let contactsUpdated = 0;
  let dealsCreated = 0;

  const createdDealIds: (number | string)[] = [];
  const createdContactIds: (number | string)[] = [];
  const createdCompanyIds: (number | string)[] = [];

  const skippedRows: Record<string, any>[] = [];

  // Caches em memória durante a importação para evitar chamadas redundantes e acelerar
  const companyCache = new Map<string, Company>();
  const contactCache = new Map<string, Contact>();
  const tagCache = new Map<string, Tag>();

  // Carrega lista de tags/etiquetas pré-existentes do CRM
  try {
    const tRes = await dataProvider.getList("tags", {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
      filter: {},
    });
    if (tRes?.data) {
      for (const t of tRes.data) {
        if (t.name) {
          tagCache.set(t.name.trim().toLowerCase(), t as Tag);
        }
      }
    }
  } catch (err) {
    console.warn("Aviso ao pré-carregar tags para importação:", err);
  }

  // Carrega lista de vendedores/usuários para resolução de responsáveis
  let salesList: any[] = [];
  try {
    const sRes = await dataProvider.getList("sales", {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: {},
    });
    salesList = sRes.data || [];
  } catch {
    salesList = [];
  }

  const resolveSalesId = (name?: string, email?: string): any => {
    if (email) {
      const e = email.trim().toLowerCase();
      const match = salesList.find((s) => s.email?.toLowerCase() === e);
      if (match) return match.id;
    }
    if (name) {
      const n = name.trim().toLowerCase();
      const match = salesList.find((s) => {
        const full = `${s.first_name || ""} ${s.last_name || ""}`
          .trim()
          .toLowerCase();
        return (
          full === n ||
          (s.first_name && s.first_name.toLowerCase() === n) ||
          (s.email && s.email.toLowerCase().includes(n))
        );
      });
      if (match) return match.id;
    }
    return options.defaultSalesId || null;
  };

  const updateProgress = (current: number, statusText: string) => {
    if (onProgress) {
      onProgress({
        current,
        total,
        percent: Math.min(100, Math.round((current / total) * 100)),
        companiesCreated,
        companiesUpdated,
        contactsCreated,
        contactsUpdated,
        dealsCreated,
        skippedCount: skippedRows.length,
        statusText,
      });
    }
  };

  updateProgress(0, "Iniciando processamento da planilha...");

  for (let i = 0; i < total; i += BATCH_SIZE) {
    const batch = spreadsheet.rawRows.slice(i, i + BATCH_SIZE);

    for (let bIndex = 0; bIndex < batch.length; bIndex++) {
      const rowIndex = i + bIndex + 1;
      const rawRow = batch[bIndex];

      try {
        // 1. Extração dos campos mapeados para cada entidade
        const extracted: Record<string, any> = {};
        const rowEmails: Array<{ email: string; type: string }> = [];
        const rowPhones: Array<{ number: string; type: string }> = [];
        const rowNotes: string[] = [];
        const rowDealTags: string[] = [];
        const rowPersonTags: string[] = [];

        const collectEmail = (rawVal: any, type: string) => {
          for (const item of parseEmailList(rawVal, type)) {
            if (
              !rowEmails.some(
                (e) => e.email.toLowerCase() === item.email.toLowerCase(),
              )
            ) {
              rowEmails.push(item);
            }
          }
        };

        const collectPhone = (rawVal: any, type: string) => {
          for (const item of parsePhoneList(rawVal, type)) {
            const clean = item.number.replace(/\D/g, "");
            if (!rowPhones.some((p) => p.number.replace(/\D/g, "") === clean)) {
              rowPhones.push(item);
            }
          }
        };

        for (const [header, fieldId] of Object.entries(mapping)) {
          if (!fieldId || fieldId === "ignore") continue;
          const rawVal = rawRow[header];
          if (
            rawVal === undefined ||
            rawVal === null ||
            String(rawVal).trim() === ""
          ) {
            continue;
          }
          const valStr = String(rawVal).trim();

          // Acúmulo de telefones de qualquer coluna mapeada (mesmo que várias usem o mesmo campo)
          if (
            fieldId === "person.phone_work" ||
            fieldId === "deal.contact_phone" ||
            fieldId === "person.phone_direct"
          ) {
            collectPhone(valStr, "Work");
          } else if (fieldId === "person.phone_home") {
            collectPhone(valStr, "Home");
          } else if (fieldId === "person.phone_other") {
            collectPhone(valStr, "Other");
          } else if (fieldId === "company.phone_number") {
            collectPhone(valStr, "Work");
            if (!extracted["company.phone_number"]) {
              extracted["company.phone_number"] = valStr;
            }
          } else if (
            fieldId.includes("phone") ||
            fieldId.includes("tel") ||
            fieldId.includes("celular")
          ) {
            const phoneType =
              fieldId.includes("home") || fieldId.includes("celular")
                ? "Home"
                : fieldId.includes("other")
                  ? "Other"
                  : "Work";
            collectPhone(valStr, phoneType);
          }
          // Acúmulo de e-mails de qualquer coluna mapeada
          else if (fieldId === "person.email_work") {
            collectEmail(valStr, "Work");
          } else if (fieldId === "person.email_home") {
            collectEmail(valStr, "Home");
          } else if (fieldId === "person.email_other") {
            collectEmail(valStr, "Other");
          } else if (fieldId === "deal.contact_email") {
            collectEmail(valStr, "Work");
          }
          // Acúmulo de notas de múltiplas colunas
          else if (fieldId === "note.text") {
            rowNotes.push(valStr);
          }
          // Acúmulo de tags/etiquetas de múltiplas colunas
          else if (fieldId === "deal.tags") {
            rowDealTags.push(valStr);
          } else if (fieldId === "person.tags") {
            rowPersonTags.push(valStr);
          }
          // Campos singulares (se mais de uma coluna tiver mapeado o mesmo campo, preserva o primeiro preenchido)
          else {
            if (!extracted[fieldId]) {
              extracted[fieldId] = valStr;
            }
          }
        }

        if (rowNotes.length > 0) {
          extracted["note.text"] = rowNotes.join("\n\n");
        }
        if (rowDealTags.length > 0) {
          extracted["deal.tags"] = rowDealTags.join("; ");
        }
        if (rowPersonTags.length > 0) {
          extracted["person.tags"] = rowPersonTags.join("; ");
        }

        // 2. Extração dos dados de Empresa
        const companyName =
          extracted["company.name"] || extracted["deal.company_name"] || "";
        const companyTaxId = extracted["company.tax_identifier"]?.replace(
          /[^0-9]/g,
          "",
        );

        // 4. Extração dos dados de Pessoa
        let personFirstName = extracted["person.first_name"] || "";
        let personLastName = extracted["person.last_name"] || "";
        const rawPersonFullName =
          extracted["person.full_name"] || extracted["deal.contact_name"] || "";

        if (rawPersonFullName) {
          const { firstName, lastName } = splitFullName(rawPersonFullName);
          personFirstName = personFirstName || firstName;
          personLastName = personLastName || lastName;
        }

        const primaryEmail = rowEmails[0]?.email;
        const primaryPhone = rowPhones[0]?.number;

        // Verifica se há qualquer indício de dados de pessoa nesta linha
        const hasContactData = Boolean(
          personFirstName ||
            personLastName ||
            rawPersonFullName ||
            rowEmails.length > 0 ||
            rowPhones.length > 0 ||
            extracted["person.title"] ||
            extracted["person.background"] ||
            extracted["person.linkedin_url"],
        );

        // Se há dados de contato mas nenhum nome formal foi informado, gera nome inteligente
        if (hasContactData && !personFirstName) {
          if (primaryEmail) {
            const userPart = primaryEmail.split("@")[0].replace(/[._-]/g, " ");
            personFirstName =
              userPart.charAt(0).toUpperCase() + userPart.slice(1);
          } else if (primaryPhone) {
            personFirstName = `Contato (${primaryPhone})`;
          } else if (companyName) {
            personFirstName = `Contato (${companyName})`;
          } else {
            personFirstName = "Contato";
          }
        }

        // 5. Extração dos dados de Negócio
        const dealAmount = extracted["deal.amount"]
          ? parseCurrencyOrNumber(extracted["deal.amount"])
          : undefined;
        const dealRawName = extracted["deal.name"];
        const dealStage = extracted["deal.stage"];
        const dealFunil = extracted["deal.funil"];
        const rawStatus = String(extracted["deal.status"] || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .trim();
        const dealStatus = /^(ganh|won|vend|fechad)/.test(rawStatus)
          ? "ganho"
          : /^(perd|lost)/.test(rawStatus)
            ? "perdido"
            : rawStatus
              ? "aberto"
              : undefined;

        // Data de criação original (ex: "2026-10-01 06:23" ou "01/10/2026")
        let dealCreatedAt: string | null = null;
        if (extracted["deal.created_at"]) {
          const raw = String(extracted["deal.created_at"]).trim();
          const br = raw.match(
            /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/,
          );
          const d = br
            ? new Date(
                Number(br[3]),
                Number(br[2]) - 1,
                Number(br[1]),
                Number(br[4] || 0),
                Number(br[5] || 0),
              )
            : new Date(raw.replace(" ", "T"));
          if (!isNaN(d.getTime())) dealCreatedAt = d.toISOString();
        }

        // 6. Validação de obrigatoriedade por tipo de importação:
        const skipReason = (() => {
          if (importType === "company" && !companyName) {
            return "Linha ignorada: importação de Organização requer o campo Razão Social / Nome.";
          }
          if (importType === "person" && !hasContactData) {
            return "Linha ignorada: importação de Pessoa requer ao menos Nome, E-mail ou Telefone.";
          }
          if (
            importType === "deal" &&
            !dealRawName &&
            !hasContactData &&
            !companyName
          ) {
            return "Linha ignorada: importação de Negócio requer ao menos o Título do Negócio, Contato ou Razão Social.";
          }
          return null;
        })();

        if (skipReason) {
          skippedRows.push({ ...rawRow, MOTIVO_DO_ERRO: skipReason });
          continue;
        }

        // Resolução dos responsáveis (sales_id) por entidade
        const companySalesId = resolveSalesId(
          extracted["company.owner_name"],
          extracted["company.owner_email"],
        );
        const contactSalesId = resolveSalesId(
          extracted["person.owner_name"],
          extracted["person.owner_email"],
        );
        const dealSalesId = resolveSalesId(
          extracted["deal.owner_name"],
          extracted["deal.owner_email"],
        );
        const taskSalesId = resolveSalesId(
          extracted["activity.owner_name"],
          extracted["activity.owner_email"],
        );

        let savedCompany: Company | null = null;
        let savedContact: Contact | null = null;
        let savedDeal: Deal | null = null;

        // -------------------------------------------------------------
        // PROCESSAMENTO DA ORGANIZAÇÃO (EMPRESA)
        // Executado sempre que houver nome de empresa, exceto em importações
        // exclusivas de Pessoa (onde ignoramos dados de empresa avulsos).
        // -------------------------------------------------------------
        if (companyName && importType !== "person") {
          const compCacheKey = companyTaxId || companyName.toLowerCase();
          savedCompany = companyCache.get(compCacheKey) || null;

          if (!savedCompany) {
            // Busca no banco por CNPJ ou Nome
            let existingComp: Company | null = null;
            try {
              if (companyTaxId) {
                const res = await dataProvider.getList<Company>("companies", {
                  pagination: { page: 1, perPage: 1 },
                  sort: { field: "id", order: "DESC" },
                  filter: { "tax_identifier@ilike": `%${companyTaxId}%` },
                });
                if (res.data?.[0]) existingComp = res.data[0];
              }

              if (!existingComp) {
                const res = await dataProvider.getList<Company>("companies", {
                  pagination: { page: 1, perPage: 1 },
                  sort: { field: "id", order: "DESC" },
                  filter: { "name@ilike": companyName },
                });
                if (res.data?.[0]) existingComp = res.data[0];
              }
            } catch {
              // segue fluxo
            }

            if (existingComp) {
              if (duplicateStrategy === "merge") {
                // Atualiza campos que vieram preenchidos
                const updatePayload: any = {};
                if (extracted["company.sector"] && !existingComp.sector)
                  updatePayload.sector = extracted["company.sector"];
                if (extracted["company.revenue"] && !existingComp.revenue)
                  updatePayload.revenue = extracted["company.revenue"];
                if (
                  extracted["company.phone_number"] &&
                  !existingComp.phone_number
                )
                  updatePayload.phone_number =
                    extracted["company.phone_number"];
                if (extracted["company.website"] && !existingComp.website)
                  updatePayload.website = extracted["company.website"];
                if (extracted["company.address"] && !existingComp.address)
                  updatePayload.address = extracted["company.address"];
                if (extracted["company.city"] && !existingComp.city)
                  updatePayload.city = extracted["company.city"];
                if (extracted["company.state_abbr"] && !existingComp.state_abbr)
                  updatePayload.state_abbr = extracted["company.state_abbr"];
                if (extracted["company.zipcode"] && !existingComp.zipcode)
                  updatePayload.zipcode = extracted["company.zipcode"];
                if (
                  extracted["company.tax_identifier"] &&
                  !existingComp.tax_identifier
                )
                  updatePayload.tax_identifier =
                    extracted["company.tax_identifier"];
                if (companySalesId && !existingComp.sales_id)
                  updatePayload.sales_id = companySalesId;

                if (Object.keys(updatePayload).length > 0) {
                  try {
                    const upd = await dataProvider.update<Company>(
                      "companies",
                      {
                        id: existingComp.id,
                        data: updatePayload,
                        previousData: existingComp,
                      },
                    );
                    savedCompany = upd.data;
                    companiesUpdated++;
                  } catch {
                    savedCompany = existingComp;
                  }
                } else {
                  savedCompany = existingComp;
                }
              } else if (duplicateStrategy === "skip") {
                savedCompany = existingComp;
              } else {
                // create_new: força criação mesmo com nome repetido
                existingComp = null;
              }
            }

            if (!existingComp) {
              // Cria nova empresa
              const createCompanyPayload: any = {
                name: companyName,
                tax_identifier: extracted["company.tax_identifier"] || null,
                sector: extracted["company.sector"] || "Outros",
                size: extracted["company.size"]
                  ? parseInt(extracted["company.size"], 10) || 10
                  : 10,
                revenue: extracted["company.revenue"] || null,
                phone_number: extracted["company.phone_number"] || null,
                website: extracted["company.website"] || null,
                linkedin_url: extracted["company.linkedin_url"] || null,
                address: extracted["company.address"] || null,
                city: extracted["company.city"] || null,
                state_abbr: extracted["company.state_abbr"] || null,
                zipcode: extracted["company.zipcode"] || null,
                description: extracted["company.description"] || null,
                sales_id: companySalesId,
                created_at: new Date().toISOString(),
              };

              const newComp = await dataProvider.create<Company>("companies", {
                data: createCompanyPayload,
              });
              savedCompany = Array.isArray(newComp.data)
                ? newComp.data[0]
                : newComp.data;
              companiesCreated++;
              if (savedCompany?.id) {
                createdCompanyIds.push(savedCompany.id);
              }
            }

            if (savedCompany) {
              companyCache.set(compCacheKey, savedCompany);
            }
          }
        }

        // -------------------------------------------------------------
        // PROCESSAMENTO DA PESSOA (CONTATO)
        // Executado sempre que houver dados de contato (nome, telefone,
        // e-mail, cargo, etc.), exceto em importações de Organização.
        // -------------------------------------------------------------
        if (hasContactData && importType !== "company") {
          const emailJsonb = [...rowEmails];
          const phoneJsonb = [...rowPhones];

          const contCacheKey =
            primaryEmail?.toLowerCase() ||
            (primaryPhone ? primaryPhone.replace(/\D/g, "") : null) ||
            `${personFirstName} ${personLastName}`.toLowerCase();
          savedContact = contactCache.get(contCacheKey) || null;

          if (savedContact && duplicateStrategy === "merge") {
            // Contato já no cache da importação: acumula novos telefones ou emails se houver
            const existingPhones: any[] = savedContact.phone_jsonb || [];
            const mergedPhones = [...existingPhones];
            let hasNewPhone = false;
            for (const newP of phoneJsonb) {
              const cleanNew = newP.number.replace(/\D/g, "");
              if (
                !existingPhones.some(
                  (p: any) => p.number?.replace(/\D/g, "") === cleanNew,
                )
              ) {
                mergedPhones.push(newP);
                hasNewPhone = true;
              }
            }

            const existingEmails: any[] = savedContact.email_jsonb || [];
            const mergedEmails = [...existingEmails];
            let hasNewEmail = false;
            for (const newE of emailJsonb) {
              const cleanNew = newE.email.toLowerCase().trim();
              if (
                !existingEmails.some(
                  (e: any) => e.email?.toLowerCase().trim() === cleanNew,
                )
              ) {
                mergedEmails.push(newE);
                hasNewEmail = true;
              }
            }

            if (hasNewPhone || hasNewEmail) {
              const updData: any = {};
              if (hasNewPhone) updData.phone_jsonb = mergedPhones;
              if (hasNewEmail) updData.email_jsonb = mergedEmails;
              try {
                const upd = await dataProvider.update<Contact>("contacts", {
                  id: savedContact.id,
                  data: updData,
                  previousData: savedContact,
                });
                const updatedContact = Array.isArray(upd.data)
                  ? upd.data[0]
                  : upd.data;
                if (updatedContact) {
                  savedContact = updatedContact as Contact;
                  contactCache.set(contCacheKey, updatedContact as Contact);
                }
              } catch {
                // ignore
              }
            }
          }

          if (!savedContact) {
            let existingContact: Contact | null = null;
            if (primaryEmail) {
              try {
                const res = await dataProvider.getList<Contact>("contacts", {
                  pagination: { page: 1, perPage: 5 },
                  sort: { field: "id", order: "DESC" },
                  filter: { q: primaryEmail },
                });
                if (res.data?.length) {
                  const match = res.data.find((c: any) => {
                    const emails: any[] = c.email_jsonb || [];
                    return emails.some(
                      (e: any) =>
                        e.email?.toLowerCase().trim() ===
                        primaryEmail.toLowerCase().trim(),
                    );
                  });
                  if (match) existingContact = match;
                  else if (res.data[0]) existingContact = res.data[0];
                }
              } catch {
                // segue
              }
            }

            if (!existingContact && personFirstName && personLastName) {
              try {
                const res = await dataProvider.getList<Contact>("contacts", {
                  pagination: { page: 1, perPage: 5 },
                  sort: { field: "id", order: "DESC" },
                  filter: {
                    "first_name@ilike": personFirstName,
                    "last_name@ilike": personLastName,
                  },
                });
                if (res.data?.[0]) existingContact = res.data[0];
              } catch {
                // segue
              }
            }

            const rawContactTags =
              extracted["person.tags"] || extracted["deal.tags"] || null;
            const contactTagIds = await resolveTagIds(
              rawContactTags,
              tagCache,
              dataProvider,
            );

            if (existingContact) {
              if (duplicateStrategy === "merge") {
                const updatePayload: any = {};
                if (savedCompany && !existingContact.company_id) {
                  updatePayload.company_id = Number(savedCompany.id);
                }
                if (extracted["person.title"] && !existingContact.title) {
                  updatePayload.title = extracted["person.title"];
                }
                if (
                  extracted["person.background"] &&
                  !existingContact.background
                ) {
                  updatePayload.background = extracted["person.background"];
                }
                if (contactSalesId && !existingContact.sales_id) {
                  updatePayload.sales_id = Number(contactSalesId);
                }

                if (contactTagIds.length > 0) {
                  const existingTags = Array.isArray(existingContact.tags)
                    ? existingContact.tags
                    : [];
                  const mergedTags = Array.from(
                    new Set([...existingTags, ...contactTagIds]),
                  );
                  if (mergedTags.length !== existingTags.length) {
                    updatePayload.tags = mergedTags;
                  }
                }

                // Mescla telefones adicionais no contato existente sem duplicar
                if (phoneJsonb.length > 0) {
                  const existingPhones: any[] =
                    existingContact.phone_jsonb || [];
                  const mergedPhones = [...existingPhones];
                  let hasNewPhone = false;
                  for (const newP of phoneJsonb) {
                    const cleanNew = newP.number.replace(/\D/g, "");
                    if (
                      !existingPhones.some(
                        (p: any) => p.number?.replace(/\D/g, "") === cleanNew,
                      )
                    ) {
                      mergedPhones.push(newP);
                      hasNewPhone = true;
                    }
                  }
                  if (hasNewPhone || existingPhones.length === 0) {
                    updatePayload.phone_jsonb = mergedPhones;
                  }
                }

                // Mescla e-mails adicionais no contato existente sem duplicar
                if (emailJsonb.length > 0) {
                  const existingEmails: any[] =
                    existingContact.email_jsonb || [];
                  const mergedEmails = [...existingEmails];
                  let hasNewEmail = false;
                  for (const newE of emailJsonb) {
                    const cleanNew = newE.email.toLowerCase().trim();
                    if (
                      !existingEmails.some(
                        (e: any) => e.email?.toLowerCase().trim() === cleanNew,
                      )
                    ) {
                      mergedEmails.push(newE);
                      hasNewEmail = true;
                    }
                  }
                  if (hasNewEmail || existingEmails.length === 0) {
                    updatePayload.email_jsonb = mergedEmails;
                  }
                }

                if (Object.keys(updatePayload).length > 0) {
                  try {
                    const upd = await dataProvider.update<Contact>("contacts", {
                      id: existingContact.id,
                      data: updatePayload,
                      previousData: existingContact,
                    });
                    savedContact = Array.isArray(upd.data)
                      ? upd.data[0]
                      : upd.data;
                    contactsUpdated++;
                  } catch {
                    savedContact = existingContact;
                  }
                } else {
                  savedContact = existingContact;
                }
              } else if (duplicateStrategy === "skip") {
                savedContact = existingContact;
              } else {
                existingContact = null;
              }
            }

            if (!existingContact) {
              const createPersonPayload: any = {
                first_name: personFirstName,
                last_name: personLastName,
                company_id: savedCompany?.id ? Number(savedCompany.id) : null,
                title: extracted["person.title"] || "",
                gender: extracted["person.gender"] || "other",
                email_jsonb: emailJsonb.length > 0 ? emailJsonb : null,
                phone_jsonb: phoneJsonb.length > 0 ? phoneJsonb : null,
                linkedin_url: extracted["person.linkedin_url"] || null,
                background: extracted["person.background"] || "",
                status: extracted["person.status"] || "cold",
                first_seen: new Date().toISOString(),
                last_seen: new Date().toISOString(),
                has_newsletter: false,
                tags: contactTagIds,
                sales_id: contactSalesId ? Number(contactSalesId) : null,
              };

              try {
                const newCont = await dataProvider.create<Contact>("contacts", {
                  data: createPersonPayload,
                });
                savedContact = Array.isArray(newCont.data)
                  ? newCont.data[0]
                  : newCont.data;
                contactsCreated++;
                if (savedContact?.id) {
                  createdContactIds.push(savedContact.id);
                }
              } catch (cErr: any) {
                console.error(
                  "Erro ao criar contato na linha",
                  rowIndex,
                  cErr?.message || cErr,
                );
              }
            }

            if (savedContact) {
              contactCache.set(contCacheKey, savedContact as Contact);
            }
          }
        }

        // -------------------------------------------------------------
        // PROCESSAMENTO DO NEGÓCIO / LEAD (OPORTUNIDADE)
        // Apenas executado quando importType === "deal".
        // Um negócio pode existir sem pessoa ou empresa vinculada.
        // -------------------------------------------------------------
        const shouldCreateDeal =
          importType === "deal" &&
          (Boolean(dealRawName) ||
            Boolean(savedContact) ||
            Boolean(savedCompany));

        if (shouldCreateDeal) {
          // Geração automática de título se omitido na planilha (regra padrão Pipedrive)
          let finalDealName = dealRawName;
          if (!finalDealName) {
            const pName = savedContact
              ? `${savedContact.first_name || ""} ${savedContact.last_name || ""}`.trim()
              : "";
            const cName = savedCompany ? savedCompany.name : "";
            if (pName && cName) {
              finalDealName = `${pName} - ${cName}`;
            } else if (cName) {
              finalDealName = `Oportunidade - ${cName}`;
            } else if (pName) {
              finalDealName = `Oportunidade - ${pName}`;
            } else {
              finalDealName = `Novo Negócio #${rowIndex}`;
            }
          }

          let targetFunilId = options.defaultFunilId || "principal";
          let targetStageId = options.defaultStageId || "prospeccao";

          // Se a planilha possuir funil informado, tenta localizar no pipeline
          if (dealFunil && options.pipelines && options.pipelines.length > 0) {
            const fNorm = dealFunil.toLowerCase().trim();
            const matchedPipe = options.pipelines.find(
              (p) =>
                p.id.toLowerCase() === fNorm ||
                p.nome.toLowerCase() === fNorm ||
                p.nome.toLowerCase().includes(fNorm),
            );
            if (matchedPipe) {
              targetFunilId = matchedPipe.id;
            }
          }

          // Se a planilha possuir etapa informada, tenta localizar na lista de etapas
          if (dealStage && options.pipelines && options.pipelines.length > 0) {
            const sNorm = dealStage.toLowerCase().trim();
            const currentPipe =
              options.pipelines.find((p) => p.id === targetFunilId) ||
              options.pipelines[0];
            if (currentPipe?.etapas) {
              const matchedStage = currentPipe.etapas.find(
                (st) =>
                  st.value.toLowerCase() === sNorm ||
                  st.label.toLowerCase() === sNorm ||
                  st.label.toLowerCase().includes(sNorm),
              );
              if (matchedStage) {
                targetStageId = matchedStage.value;
              }
            }
          }

          // Validação de temperatura (frio, morno, quente)
          let dealTemperatura = options.defaultTemperatura || "morno";
          if (extracted["deal.temperatura"]) {
            const t = extracted["deal.temperatura"].toLowerCase().trim();
            if (t === "frio" || t === "morno" || t === "quente") {
              dealTemperatura = t;
            } else if (t.includes("fri")) {
              dealTemperatura = "frio";
            } else if (t.includes("quente")) {
              dealTemperatura = "quente";
            }
          }

          // Tratamento de data de previsão de fechamento
          let expectedClosingDate: string | null = null;
          if (extracted["deal.expected_closing_date"]) {
            const rawDateStr = extracted["deal.expected_closing_date"].trim();
            const brMatch = rawDateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (brMatch) {
              const [, day, month, year] = brMatch;
              expectedClosingDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
            } else {
              const d = new Date(rawDateStr);
              if (!isNaN(d.getTime())) {
                expectedClosingDate = d.toISOString().split("T")[0];
              }
            }
          }

          // Processamento dos Detalhes do Negócio (custom_fields)
          const customFields: Record<string, any> = {};

          // Carrega defaults normalizando as chaves
          if (options.dealCustomFieldsDefaults) {
            for (const [k, v] of Object.entries(
              options.dealCustomFieldsDefaults,
            )) {
              if (v !== undefined && v !== null && v !== "") {
                const norm = k
                  .replace(/^(deal\.)?/, "")
                  .replace(/^(custom_fields\.)?/, "");
                const clean = norm.replace(/^(cf_)+/, "");
                customFields[norm] = v;
                customFields[`cf_${clean}`] = v;
                customFields[clean] = v;
              }
            }
          }

          for (const [key, val] of Object.entries(extracted)) {
            if (
              (key.startsWith("deal.cf_") ||
                key.startsWith("deal.custom_fields.") ||
                key.startsWith("deal.cf")) &&
              val !== undefined &&
              val !== ""
            ) {
              const raw = key
                .replace(/^deal\./, "")
                .replace(/^custom_fields\./, "");
              const clean = raw.replace(/^(cf_)+/, "");
              customFields[raw] = val;
              customFields[`cf_${clean}`] = val;
              customFields[clean] = val;
            }
          }

          // Resolução de tags / etiquetas do negócio
          const rawDealTags =
            extracted["deal.tags"] || extracted["person.tags"] || null;
          const dealTagIds = await resolveTagIds(
            rawDealTags,
            tagCache,
            dataProvider,
          );

          const dealPayload: any = {
            name: finalDealName,
            amount: dealAmount || 0,
            funil_id: targetFunilId,
            stage_id: targetStageId,
            stage: targetStageId,
            status:
              dealStatus === "ganho" || dealStatus === "perdido"
                ? dealStatus
                : "aberto",
            expected_closing_date: expectedClosingDate,
            category: extracted["deal.category"] || null,
            temperatura: dealTemperatura,
            description: extracted["deal.description"] || null,
            origem: extracted["deal.source"] || options.defaultOrigem || null,
            motivo_perda: extracted["deal.lost_reason"] || null,
            tags: dealTagIds,
            custom_fields: customFields,
            company_id: savedCompany?.id ? Number(savedCompany.id) : null,
            contact_ids: savedContact?.id ? [Number(savedContact.id)] : [],
            sales_id: dealSalesId ? Number(dealSalesId) : null,
            index: 0,
            created_at: dealCreatedAt || new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };

          if (dealStatus === "ganho") {
            dealPayload.won_at = new Date().toISOString();
          } else if (dealStatus === "perdido") {
            dealPayload.lost_at = new Date().toISOString();
          }

          try {
            const newDeal = await dataProvider.create<Deal>("deals", {
              data: dealPayload,
            });
            savedDeal = Array.isArray(newDeal.data)
              ? newDeal.data[0]
              : newDeal.data;
            dealsCreated++;
            if (savedDeal?.id) {
              createdDealIds.push(savedDeal.id);
            }
          } catch (dErr: any) {
            console.error(
              "Erro ao criar oportunidade/negócio na linha",
              rowIndex,
              dErr?.message || dErr,
            );
            skippedRows.push({
              ...rawRow,
              MOTIVO_DO_ERRO: `Oportunidade não criada na linha ${rowIndex}: ${dErr?.message || "Erro ao salvar negócio"}`,
            });
          }
        }

        // -------------------------------------------------------------
        // PROCESSAMENTO DE ATIVIDADE OU ANOTAÇÃO INICIAL
        // -------------------------------------------------------------
        if (extracted["activity.text"]) {
          try {
            const taskPayload: any = {
              text: extracted["activity.text"],
              type: extracted["activity.type"]?.toLowerCase() || "tarefa",
              due_date: extracted["activity.due_date"]
                ? new Date(extracted["activity.due_date"]).toISOString()
                : new Date().toISOString(),
              deal_id: savedDeal?.id || null,
              contact_id: savedContact?.id || null,
              company_id: savedCompany?.id || null,
              sales_id: taskSalesId,
              created_at: new Date().toISOString(),
            };
            await dataProvider.create("tasks", { data: taskPayload });
          } catch {
            // ignore
          }
        }

        if (extracted["note.text"]) {
          try {
            if (savedDeal) {
              await dataProvider.create("deal_notes", {
                data: {
                  deal_id: savedDeal.id,
                  text: extracted["note.text"],
                  date: new Date().toISOString(),
                  sales_id: dealSalesId,
                },
              });
            } else if (savedContact) {
              await dataProvider.create("contact_notes", {
                data: {
                  contact_id: savedContact.id,
                  text: extracted["note.text"],
                  date: new Date().toISOString(),
                  sales_id: contactSalesId,
                },
              });
            }
          } catch {
            // ignore
          }
        }
      } catch (rowErr: any) {
        skippedRows.push({
          ...rawRow,
          MOTIVO_DO_ERRO: `Erro técnico na linha ${rowIndex}: ${rowErr?.message || "Falha desconhecida"}`,
        });
      }
    }

    updateProgress(
      Math.min(total, i + BATCH_SIZE),
      `Processando linhas ${i + 1} a ${Math.min(total, i + BATCH_SIZE)} de ${total}...`,
    );
  }

  updateProgress(total, "Importação concluída com sucesso!");

  let skippedCsvContent: string | undefined = undefined;
  if (skippedRows.length > 0) {
    skippedCsvContent = Papa.unparse(skippedRows, { delimiter: ";" });
  }

  const rollbackInfo: ImportBatchRollbackInfo = {
    importId: `import_${Date.now()}`,
    timestamp: new Date().toISOString(),
    fileName: spreadsheet.fileName || "Planilha",
    dealIds: createdDealIds,
    contactIds: createdContactIds,
    companyIds: createdCompanyIds,
    totalCreated:
      createdDealIds.length +
      createdContactIds.length +
      createdCompanyIds.length,
  };

  // Salva no localStorage para permitir "Desfazer última importação" a qualquer momento
  if (
    typeof window !== "undefined" &&
    window.localStorage &&
    rollbackInfo.totalCreated > 0
  ) {
    try {
      window.localStorage.setItem(
        "dotus_last_import_batch",
        JSON.stringify(rollbackInfo),
      );
      window.dispatchEvent(new Event("dotus_import_batch_updated"));
    } catch {
      // ignore
    }
  }

  return {
    total,
    processed: total - skippedRows.length,
    companiesCreated,
    companiesUpdated,
    contactsCreated,
    contactsUpdated,
    dealsCreated,
    skippedCount: skippedRows.length,
    hasErrors: skippedRows.length > 0,
    skippedCsvContent,
    rollbackInfo,
  };
}
